/**
 * Enhanced Medication Card Component with Image Support
 * Displays medication information with package and pill images from MinIO
 */

import React from "react";
import {
  Pill,
  Calendar,
  Clock,
  AlertTriangle,
  Edit2,
  Trash2,
  Image as ImageIcon,
  Package,
} from "lucide-react";
import { MedicationDisplay } from "@/types/fhir";
import { medicationDisplayHelpers } from "@/services/api/fhirMedicationService";
import { useAuthenticatedImage } from "@/hooks/useAuthenticatedImage";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { useI18nStore } from "@/stores/i18nStore";
import type { Translation } from "@/types/i18n";

/** FHIR MedicationStatement status code -> translated label key. */
const STATUS_LABEL_KEYS: Record<string, keyof Translation> = {
  active: "medxStatusActive",
  recorded: "medxStatusActive",
  completed: "medxStatusCompleted",
  stopped: "medxStatusStopped",
  draft: "medxStatusDraft",
  "on-hold": "medxStatusOnHold",
  "entered-in-error": "medxStatusEnteredInError",
  intended: "medxStatusIntended",
  "not-taken": "medxStatusNotTaken",
  unknown: "medxStatusUnknown",
};

interface EnhancedMedicationCardProps {
  medication: MedicationDisplay;
  onEdit: (medication: MedicationDisplay) => void;
  onDelete: (id: string) => void;
  getStatusBadgeClass: (status: string) => string;
}

export const EnhancedMedicationCard: React.FC<EnhancedMedicationCardProps> = ({
  medication,
  onEdit,
  onDelete,
  getStatusBadgeClass,
}) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const statusKey = STATUS_LABEL_KEYS[medication.status];
  const statusLabel = statusKey ? t[statusKey] : medication.status;
  const isExpiring =
    medication.raw && medicationDisplayHelpers.isExpiringSoon(medication.raw);
  const isExpired =
    medication.raw && medicationDisplayHelpers.isExpired(medication.raw);

  // Extract image URLs from FHIR raw data
  // The stored URLs are API paths that need the bearer token.
  const packageImageUrl = useAuthenticatedImage(medication.packageImageUrl);
  const medicationImageUrl = useAuthenticatedImage(
    medication.medicationImageUrl,
  );
  const hasImages = packageImageUrl || medicationImageUrl;

  return (
    <div className="p-6 hover:bg-gray-50 transition-colors border-b border-gray-200">
      <div className="flex gap-4">
        {/* Image Section (if available) */}
        {hasImages && (
          <div className="flex-shrink-0">
            <div className="flex gap-2">
              {/* Package Image */}
              {packageImageUrl && (
                <div className="relative group">
                  <img
                    src={packageImageUrl}
                    alt={t.medxCardPackageAlt}
                    className="w-20 h-20 object-cover rounded-lg border-2 border-blue-200 shadow-sm hover:shadow-md transition-shadow"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-opacity flex items-center justify-center">
                    <Package className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 bg-blue-600 rounded-full p-1">
                    <Package className="h-3 w-3 text-white" />
                  </div>
                </div>
              )}

              {/* Medication/Pill Image */}
              {medicationImageUrl && (
                <div className="relative group">
                  <img
                    src={medicationImageUrl}
                    alt={t.medxCardPillAlt}
                    className="w-20 h-20 object-cover rounded-lg border-2 border-green-200 shadow-sm hover:shadow-md transition-shadow"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-opacity flex items-center justify-center">
                    <Pill className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 bg-green-600 rounded-full p-1">
                    <Pill className="h-3 w-3 text-white" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* No Images Placeholder */}
        {!hasImages && (
          <div className="flex-shrink-0">
            <div className="w-20 h-20 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
              <ImageIcon className="h-8 w-8 text-gray-400" />
            </div>
          </div>
        )}

        {/* Content Section */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <Pill className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {medication.name}
            </h3>
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getStatusBadgeClass(medication.status)}`}
            >
              {statusLabel}
            </span>
            {isExpiring && !isExpired && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 flex-shrink-0 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {t.medxCardExpiringSoon}
              </span>
            )}
            {isExpired && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex-shrink-0 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {t.medxCardExpired}
              </span>
            )}
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
            {medication.category && (
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  {t.medxCardCategory}
                </p>
                <p className="text-sm font-medium text-gray-900">
                  {medication.category}
                </p>
              </div>
            )}
            {medication.dosage && (
              <div>
                <p className="text-xs text-gray-500 mb-1">{t.medxCardDosage}</p>
                <p className="text-sm font-medium text-gray-900">
                  {medication.dosage}
                </p>
              </div>
            )}
            {medication.frequency && (
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  {t.medxCardFrequency}
                </p>
                <p className="text-sm font-medium text-gray-900">
                  {medication.frequency}
                </p>
              </div>
            )}
            {medication.startDate && (
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  {t.medxCardStartDate}
                </p>
                <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {fmt.date(medication.startDate)}
                </p>
              </div>
            )}
          </div>

          {/* Instructions */}
          {medication.instructions && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>{t.medxCardInstructions}</strong>{" "}
                {medication.instructions}
              </p>
            </div>
          )}

          {/* Image Status Indicator */}
          {hasImages && (
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              <ImageIcon className="h-3 w-3" />
              <span>
                {packageImageUrl && medicationImageUrl
                  ? t.medxCardBothPhotographed
                  : packageImageUrl
                    ? t.medxCardPackagePhotographed
                    : t.medxCardPillPhotographed}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col items-center gap-2">
          <button
            onClick={() => onEdit(medication)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title={t.medxCardEdit}
            aria-label={t.medxCardEdit}
          >
            <Edit2 className="h-5 w-5" />
          </button>
          <button
            onClick={() => onDelete(medication.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title={t.medxCardDelete}
            aria-label={t.medxCardDelete}
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedMedicationCard;
